import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSiteConfig } from 'src/contexts/SiteGuardContext';
import { findRoute, resolveRouteTitle } from 'src/router/routeA11y';
import Layout, { UserLayout } from 'src/layouts';
import SuspenseProgressBridge from 'src/components/SuspenseProgressBridge';
import ProtectedRoute from 'src/router/ProtectedRoute';
import { protectedRoutes, publicRoutes } from 'src/router/routes';
import { routeType } from 'src/utils/proptypes';

interface OrgThemeColors {
   lightPrimary?: string;
   primary?: string;
   secondary?: string;
   view?: string;
   lightView?: string;
   callToAction?: string;
   headerText?: string;
   heroText?: string;
   startBackground?: string;
   endBackground?: string;
}

interface SiteVisualBranding {
   pallate?: {
      colorLightPrimary?: string;
      colorPrimary?: string;
      colorSecondary?: string;
      colorView?: string;
      colorLightView?: string;
      colorCallToAction?: string;
      colorHeaderText?: string;
      colorHeroText?: string;
      colorStartBackground?: string;
      colorEndBackground?: string;
   };
}

const NotFoundPage = lazy(() => import('src/pages/notFound'));

// Page titles (WCAG 2.4.2, NPT-1466). NPT-1072 removed the imperative
// document.title write that ran on every route change because it overrode
// the per-route +title.ts / <SEO> titles after hydration. The write is back
// in a narrower form: routes whose page owns its title (`titleSource`, see
// routes.tsx) are left alone, so admin/auth/user routes get a descriptive
// "{title} | {siteName}" tab title again without regressing NPT-1072.
// React 19 inserts a hoisted <title> BEFORE any existing one, so an <SEO>
// title always wins over a stale imperative one when the user moves on
// to a page that renders <SEO>.
//
// Route changes are also announced to assistive technology through a
// polite live region that is mounted once, here, so it exists before its
// text changes (NPT-1464); focus moves to <main> via RouteFocus in the
// layout (WCAG 2.4.3).

const allRoutes = [...publicRoutes, ...protectedRoutes];

const FALLBACK_SITE_NAME = '';

const Router = () => {
   const location = useLocation();
   const { t } = useTranslation();
   const { siteConfig } = useSiteConfig();
   const siteName = siteConfig?.displayName || FALLBACK_SITE_NAME;

   // `location.key` is "default" until the first in-app navigation; sticky so
   // a Back to the initial entry still counts as having navigated.
   const initialKey = useRef(location.key);
   const navigatedRef = useRef(false);
   if (location.key !== initialKey.current) navigatedRef.current = true;
   const navigated = navigatedRef.current;

   const [announcement, setAnnouncement] = useState('');

   useEffect(() => {
      const route = findRoute(allRoutes, location.pathname);
      const title = resolveRouteTitle(route, navigated, siteName);
      if (title) document.title = title;
      if (navigated && route?.title) setAnnouncement(t('a11y.routeChanged', { title: route.title }));
   }, [location.pathname, navigated, siteName, t]);

   return (
      <>
         <Routes>
            {publicRoutes.map((route: routeType, idx: number) => {
               return <Route path={route.path} element={<Layout route={route} navigated={navigated} />} key={idx} />;
            })}
            {protectedRoutes.map((route: routeType, idx: number) => {
               return (
                  <Route
                     key={idx}
                     path={route.path}
                     element={
                        <ProtectedRoute requiredPermissions={route.permissions || []}>
                           <Layout route={route} navigated={navigated} />
                        </ProtectedRoute>
                     }
                  />
               );
            })}
            <Route
               path="*"
               element={
                  <UserLayout>
                     <Suspense fallback={<SuspenseProgressBridge />}>
                        <NotFoundPage />
                     </Suspense>
                  </UserLayout>
               }
            />
         </Routes>
         <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
         </div>
      </>
   );
};

export default Router;
